import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import AsyncButton from '@wojtekmaj/react-async-button';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import CheckboxField from '@rewardopl/react-ui/checkbox_field';
import InputField from '@rewardopl/react-ui/input_field';

import { post } from '@rewardopl/utils/network';

import { ButtonWrapper, Form } from '../login/email/form.styles';

import MarketingControls from '../../common/marketing_controls';
import MarketingDisclaimers from '../../common/marketing_disclaimers';
import PrivacyPolicyLink from '../../privacy_policy_link';
import TermsAndConditionsLink from '../../terms_and_conditions_link';
import EmailTypoDialog from './email_typo_dialog';

import useLocationParam from '../../../hooks/useLocationParam';

import { correctEmailTypo } from '../../../utils/email_typos';
import { isPasswordValid } from '../../../utils/password';

import { cardSubscriptionsState, maybeCurrentUserState } from '../../../store';

import { APP_ID, VENDOR_CARD_ID } from '../../../constants';

import type { CardSubscription, User } from '@rewardopl/types';

const pendingConfig = {
  disabled: true,
};

export default function RegisterForm() {
  const location = useLocation();
  const [referralId] = useLocationParam('referral_id');
  const navigate = useNavigate();
  const setCurrentUser = useSetAtom(maybeCurrentUserState);
  const setCardSubscriptions = useSetAtom(cardSubscriptionsState);
  const registerErrorString = useTranslation('Failed to register');
  const userAlreadyExistsString = useTranslation('User already exists');
  const passwordInvalidString = useTranslation(
    'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
  );

  const { email: stateEmail = '', returnPath = '/' } = (location.state || {}) as {
    email?: string;
    returnPath?: string;
  };

  const [email, setEmail] = useState(stateEmail);
  const [password, setPassword] = useState('');
  const [marketingEmailAccepted, setMarketingEmailAccepted] = useState(false);
  const [marketingSmsAccepted, setMarketingSmsAccepted] = useState(false);
  const marketingAccepted = marketingEmailAccepted || marketingSmsAccepted;

  const [isEmailTypoDialogOpen, setIsEmailTypoDialogOpen] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null);

  const action = '/api/users';

  function onBlur() {
    if (!email) {
      return;
    }

    const suggestedEmail = correctEmailTypo(email);

    if (suggestedEmail) {
      setOriginalEmail(email);
      setSuggestedEmail(suggestedEmail);
      setIsEmailTypoDialogOpen(true);
    }
  }

  function onClose() {
    setIsEmailTypoDialogOpen(false);
  }

  async function onClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.preventDefault();

    const submitButton = event.currentTarget;
    const form = submitButton.form;

    if (!form || !form.reportValidity()) {
      return;
    }

    const formData = new FormData(form);

    try {
      const user = (await post(action, {
        headers: {
          'app-id': APP_ID,
        },
        body: formData,
      })) as User;

      const response = (await post('/api/users/current/card_subscriptions', {
        body: { card_id: VENDOR_CARD_ID },
      })) as CardSubscription;

      setCurrentUser(user);
      setCardSubscriptions([response]);
    } catch (err) {
      const error = err;

      const errorString = (() => {
        if (!(error instanceof Error)) {
          return registerErrorString;
        }

        switch (error.message) {
          case 'Password does not meet complexity requirements':
            return passwordInvalidString;
          case 'User already exists':
            return userAlreadyExistsString;
          default:
            return registerErrorString;
        }
      })();

      toast.error(errorString);

      if (error instanceof Error && error.message === 'User already exists') {
        navigate('/login/email', { state: { email: formData.get('email'), returnPath } });
      }
    }
  }

  return (
    <>
      <Form action={action} method="POST">
        <input name="referral_id" value={referralId} type="hidden" />
        <InputField
          label="Email"
          maxLength={320}
          name="email"
          onBlur={onBlur}
          onChange={setEmail}
          placeholder="email@example.com"
          type="email"
          value={email}
        />
        <InputField
          autoComplete="new-password"
          hint="Min. 8 characters, uppercase letter, lowercase letter, number, special character"
          label="Password"
          minLength={8}
          name="password"
          onChange={(nextPassword, event) => {
            setPassword(nextPassword);

            event.target.setCustomValidity(
              isPasswordValid(nextPassword, email) ? '' : passwordInvalidString,
            );
          }}
          placeholder="••••••••"
          type="password"
          value={password}
        />
        <CheckboxField
          name="terms_accepted"
          label={
            <>
              <T>I have read and agree to the</T> <TermsAndConditionsLink /> <T>and</T>{' '}
              <PrivacyPolicyLink />.
            </>
          }
          required
        />
        <MarketingControls
          marketingAccepted={marketingAccepted}
          marketingEmailAccepted={marketingEmailAccepted}
          marketingSmsAccepted={marketingSmsAccepted}
          setMarketingEmailAccepted={setMarketingEmailAccepted}
          setMarketingSmsAccepted={setMarketingSmsAccepted}
        />
        <MarketingDisclaimers />
        <ButtonWrapper>
          <AsyncButton
            as={Button}
            className="primary"
            onClick={onClick}
            pendingConfig={pendingConfig}
            type="submit"
          >
            <T>Register</T>
          </AsyncButton>
        </ButtonWrapper>
      </Form>
      {suggestedEmail ? (
        <EmailTypoDialog
          isOpen={isEmailTypoDialogOpen}
          onClose={onClose}
          originalEmail={originalEmail}
          setEmail={setEmail}
          suggestedEmail={suggestedEmail}
        />
      ) : null}
    </>
  );
}
